import {
    Scrollbar,
    Icon,
    ListItem,
    Button,
    PriorityIcon,
    Title,
    Text
} from '@priority-software/priority-style-react';
import React from 'react';
import cx from 'classnames';

import {highlightText} from '../../_legacy/library/utils/highlightText';
import text from './texts';
import styles from './styles.module.scss';
import {isRTL} from '../../Localization';
import {BufferData} from './useF9Dialog';
import {EmptyListMessage} from '../../features/Form/components/EmptyListMessage';
import {ModernTooltip} from '../../components/Common/Tooltip/ModernTooltip';
import {f9DialogId} from './constants';
import {useScroll} from '../dropdown/useScroll';
import {isRTLString} from '@priority-software/priority-utils/lib/strings';

type DeletedItem = {element: BufferData; isOpen: boolean};

type Props = {
    isCopyAction: boolean;
    data: BufferData[];
    isOpenDropdown: boolean;
    search: string;
    handlePasteItem: (item: BufferData) => void;
    deleteElement: ({element, isOpen}: DeletedItem) => void;
    handleOpenDropdown: () => void;
    focusedIndex: number;
};

export const Dropdown = ({
    isCopyAction,
    data,
    search,
    isOpenDropdown,
    handleOpenDropdown,
    handlePasteItem,
    deleteElement,
    focusedIndex
}: Props) => {
    const {itemsRef} = useScroll({items: data, focusedItemIndex: focusedIndex});

    return (
        <>
            <Button
                skin="text"
                componentClasses={styles.triggerBtn}
                onClick={handleOpenDropdown}
                prefixIcon={
                    <Icon
                        icon={PriorityIcon.arrowDown}
                        componentClasses={cx({[styles.openDropdown]: isOpenDropdown})}
                    />
                }
            >
                <Title skin="h5"> {text.buffer}</Title>
            </Button>

            {isOpenDropdown && (
                <>
                    <div className={cx(styles.itemLabel)}>
                        <Text skin="paragraph-1" textColor="secondary-300">
                            {text.no_field}
                        </Text>
                        <Text skin="paragraph-1" textColor="secondary-300">
                            {text.value_field_in_list}
                        </Text>
                        <Text skin="paragraph-1" textColor="secondary-300">
                            {text.description_Field}
                        </Text>
                    </div>
                    <Scrollbar autoHeight autoHeightMax={200} rtl={isRTL()}>
                        {data.map((item, index) => {
                            const directionClass = isRTLString(item.value)
                                ? 'forceRtl'
                                : 'forceLtr';
                            return (
                                <ListItem
                                    ref={itemsRef.current[index]}
                                    componentClasses={cx(styles.menuItem, 'blockAction', {
                                        [styles.cancelHover]: isCopyAction,
                                        [styles.showDeleteIcon]: focusedIndex === index,
                                        [styles.selectBorder]:
                                            focusedIndex === index && !isCopyAction
                                    })}
                                    key={item.id}
                                    isActive={focusedIndex === index}
                                    isFocused={false}
                                    onClick={() => handlePasteItem(item)}
                                >
                                    <Text skin="paragraph-1" textColor="secondary-500">
                                        #{highlightText(item.id, search)}
                                    </Text>

                                    <ModernTooltip
                                        className={cx(styles.tooltip, directionClass)}
                                        tooltip={item.value}
                                        position="top"
                                        portalId={f9DialogId}
                                        wrapperClassname={styles.ellipsis}
                                    >
                                        <Text
                                            skin="paragraph-1"
                                            textColor="primary-700"
                                            componentClasses={cx(styles.ellipsis)}
                                        >
                                            <span className={directionClass}>
                                                {highlightText(item.value, search)}
                                            </span>
                                        </Text>
                                    </ModernTooltip>
                                    <ModernTooltip
                                        className={cx(
                                            styles.tooltip,
                                            directionClass,
                                            styles.ellipsis
                                        )}
                                        portalId={f9DialogId}
                                        tooltip={item.desc}
                                        position="top"
                                        wrapperClassname={styles.ellipsis}
                                    >
                                        <Text
                                            skin="paragraph-1"
                                            textColor="secondary-500"
                                            componentClasses={styles.ellipsis}
                                        >
                                            <span className={directionClass}>
                                                {highlightText(item.desc, search)}
                                            </span>
                                        </Text>
                                    </ModernTooltip>
                                    <Icon
                                        onClick={e => {
                                            e.stopPropagation();
                                            deleteElement({element: item, isOpen: true});
                                        }}
                                        skin="standart"
                                        icon={PriorityIcon.delete}
                                        componentClasses={cx(styles.deleteIcon)}
                                    />
                                </ListItem>
                            );
                        })}
                        {!data.length && (
                            <EmptyListMessage
                                search={search}
                                emptyDataText={text.text_for_empty_buffer}
                                emptySearchText={text.dont_find_search}
                            />
                        )}
                    </Scrollbar>
                </>
            )}
        </>
    );
};
